import React, { useCallback, useEffect, useState } from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import connect from 'react-redux/lib/connect/connect';
import { getDaysInMonth, getYear } from 'date-fns';
import { bindActionCreators } from 'redux';
import {abbreviatedMonths} from '../../../../constants/months';
import { adminActions } from '../../../../state/modules/admin';
import { LoadingButton, SelectFormField } from '../../../components';
import { DataTable } from './data-table';
import he from 'he';
import {downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream} from '../../../../utils';

const queryUserStatsTakeNumber = 20;

const defaultCpdMonth = '6';

const defaultCpdDay = '30';

const requiredCpdCategoryPointsKey = (cpdCategoryId) => `${cpdCategoryId}-required`;

const availableCpdCategoryPointsKey = (cpdCategoryId) => `${cpdCategoryId}-available`;

/**
 * Flatten the CPD categories with its children and apply the parent CPD category name to the children.
 * Parent CPD category won't be displayed.
 */
const flattenCpdCategories = (cpdCategories) => [
  ...cpdCategories.filter(category => category.children.length === 0),
  ...cpdCategories.flatMap((category) => {
    if (category.children) {
      return category.children.map((child) => ({
        ...child,
        cpdCategoryName: `${category.cpdCategoryName} - ${child.cpdCategoryName}`
      }));
    }
    
    return [];
  })
];

/**
 * Available CPD points for the CPD category based on the earned CPD points of the user.
 * If a CPD category has 10 points total to be earned, and the user earned 5 points already in the category, the available points is 5.
 * @param totalCpdCategoryCpdPoints total CPD points available for the CPD category to be earned.
 * @param validCpdEarned already earned CPD points for the user in the CPD category.
 * @returns CPD points available to be earned in the CPD category.
 */
const calculateAvailableCpdCategoryPoints = (totalCpdCategoryCpdPoints, validCpdEarned) => Math.max((totalCpdCategoryCpdPoints - validCpdEarned).toFixed(2), 0)

/**
 * Produces the table or CSV report columns for the users CPD progress.
 * The CPD Categories (area of advice) columns are dynamically generated based on the provided CPD categories stats.
 * @param usersCpdStats The CPD stats retrieved from user's CPD progress reports.
 * @returns CPD progress report's columns.
 */
const cpdProgressReportColumns = (usersCpdStats) => {
  const allCpdCategoryIds = new Set(usersCpdStats.flatMap((stat) => {
    const allCpdCategories = flattenCpdCategories(stat.cpdCategorys);

    return allCpdCategories.flatMap(cpdCategory => cpdCategory.cpdCategoryId);
  }));

  const allUserCpdCategories = usersCpdStats.flatMap((stat) => flattenCpdCategories(stat.cpdCategorys));

  const allCpdCategories = [...allCpdCategoryIds || []].map((cpdCategoryId) => {
    const cpdCategory = allUserCpdCategories.find(cpdCategory => cpdCategory.cpdCategoryId === cpdCategoryId);

    return cpdCategory;
  });

  // Duplicate the CPD Categories stats for category required points and category available points column names.
  const cpdCategoriesStatsColumns = allCpdCategories.reduce((acc, stat) => ([ ...acc, { required: stat }, { available: stat } ]), []);

  return [
    {
      columnName: 'name',
      display: () => 'User Name'
    },
    {
      columnName: 'email',
      display: () => 'Email Address'
    },
    {
      columnName: 'businessName',
      display: () => 'Business Name'
    },
    {
      columnName: 'industry',
      display: () => 'Industry'
    },
    {
      columnName: 'totalRequired',
      display: () => 'Total CPD Required'
    },
    {
      columnName: 'totalReceived',
      display: () => 'Total CPD Received'
    },
    {
      columnName: 'totalQtrpRequired',
      display: () => 'Total TPB CPE Required'
    },
    {
      columnName: 'totalQtrpReceived',
      display: () => 'Total TPB CPE Received'
    },
    {
      columnName: 'totalRmRequired',
      display: () => 'Total RM CPD Required'
    },
    {
      columnName: 'totalRmReceived',
      display: () => 'Total RM CPD Received'
    },
    ...cpdCategoriesStatsColumns.map(cpdCategoryStatCol => ({
      columnName: cpdCategoryStatCol.required ?
        requiredCpdCategoryPointsKey(cpdCategoryStatCol.required.cpdCategoryId) : availableCpdCategoryPointsKey(cpdCategoryStatCol.available.cpdCategoryId),
      display: () => cpdCategoryStatCol.required ? 
        `${he.decode(cpdCategoryStatCol.required.cpdCategoryName)} Required` : `${he.decode(cpdCategoryStatCol.available.cpdCategoryName)} Available`
    }))
  ];
};

/**
 * Produces the table or CSV report rows for the users CPD progress.
 * The row data key is based on the "columnName" of the generated columns.
 * @param usersCpdStats The CPD stats retrieved from user's CPD progress reports.
 * @param usersCpdCategorySearchTiles Search tiles for the CPD categories of the users, used to calculate available CPD points.
 * @returns CPD progress report's rows.
 */
const cpdProgressReportRows = (usersCpdStats, usersCpdCategorySearchTiles) => usersCpdStats.map((userCpdStat, index) => {
  const userCpdCategoriesStats = flattenCpdCategories(userCpdStat.cpdCategorys);

  const cpdCategorySearchTiles = usersCpdCategorySearchTiles.find(usersCpdCategorySearchTile => usersCpdCategorySearchTile.userId === userCpdStat.userId);

  const userCpdCategoriesStatsRowData = userCpdCategoriesStats.reduce((acc, userCpdCategoryStat) => {
    const cpdCategorySearchTile = cpdCategorySearchTiles.cpdCategorySearchTiles.find(tile => tile.cpdCategoryId === userCpdCategoryStat.cpdCategoryId);

    const totalCpdCategoryCpdPoints = cpdCategorySearchTile.searchTiles.reduce((acc, tile) => acc + tile.cpdPoints, 0);

    return {
      ...acc,
      [requiredCpdCategoryPointsKey(userCpdCategoryStat.cpdCategoryId)]: userCpdCategoryStat.derivedStat.shortFall,
      [availableCpdCategoryPointsKey(userCpdCategoryStat.cpdCategoryId)]: calculateAvailableCpdCategoryPoints(totalCpdCategoryCpdPoints, userCpdCategoryStat.primaryStat.validCpd)
    };
  }, {});

  const qtrpGoal = userCpdStat.goals === undefined ? undefined : userCpdStat.goals.find((goalStat) => goalStat.goal.name === 'TPB CPE');

  const rmGoal = userCpdStat.goals === undefined ? undefined : userCpdStat.goals.find((goalStat) => goalStat.goal.name === 'RM CPD');

  return {
    id: `progress-report-${index}`,
    name: `${userCpdStat.firstName} ${userCpdStat.lastName}`,
    email: userCpdStat.email,
    businessName: userCpdStat.businessName,
    industry: userCpdStat.industryName,
    totalRequired: userCpdStat.derivedStat.shortFall,
    totalReceived: userCpdStat.primaryStat.validCpd,
    totalQtrpRequired: qtrpGoal === undefined ? 0 : qtrpGoal.shortFall,
    totalQtrpReceived: qtrpGoal === undefined ? 0 : qtrpGoal.completed,
    totalRmRequired: rmGoal === undefined ? 0 : rmGoal.shortFall,
    totalRmReceived: rmGoal === undefined ? 0 : rmGoal.completed,
    ...userCpdCategoriesStatsRowData
  };
});

/**
 * Renders the page to display and download User CPD Progress report.
 */
const UserCpdProgressReport = ({ 
  getUserCpdProgressReport, 
  resetUserCpdProgressReport, 
  userCpdProgressReport, 
  isFetchingReport, 
  downloadUserCpdProgressReport, 
  downloadedUserCpdProgressReport,
  isDownloadingUserCpdProgressReport
}) => {
  const usersCpdStats = userCpdProgressReport === undefined ? [] : userCpdProgressReport.usersCpdStats;

  const usersCpdCategoriesSearchTiles = userCpdProgressReport === undefined ? [] : userCpdProgressReport.usersCpdCategorySearchTiles;

  const totalUserCpdStats = userCpdProgressReport === undefined ? 0 : userCpdProgressReport.total;

  const downloadedUsersCpdStats = downloadedUserCpdProgressReport === undefined ? [] : downloadedUserCpdProgressReport.usersCpdStats;

  const totalDownloadedUserCpdStats = downloadedUserCpdProgressReport === undefined ? 0 : downloadedUserCpdProgressReport.total;

  const downloadedUsersCpdCategoriesSearchTiles = downloadedUserCpdProgressReport === undefined ? [] : downloadedUserCpdProgressReport.usersCpdCategorySearchTiles;

  const currentYear = getYear(new Date());

  const [selectedMonth, setSelectedMonth] = useState(defaultCpdMonth);

  const [selectedDay, setSelectedDay] = useState(defaultCpdDay);

  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [downloadingReport, setDownloadingReport] = useState(false);

  const [downloadingProgressPercentage, setDownloadingProgressPercentage] = useState(undefined);

  const [downloadFinishedNoResult, setDownloadFinishedNoResult] = useState(false);

  const numberOfDaysInSelectedMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));

  const dayOptions = [...Array(numberOfDaysInSelectedMonth).keys()];

  // Only display option for current year and previous year for now.
  const offsetYears = [
    currentYear - 1,
    currentYear
  ];

  // Load the CPD progress report for either display or download.
  // The download will trigger continuous fetch until all users match with the filter are downloaded.
  // Specify the action to fire to either download or query the report.
  const loadCpdProgressReport = useCallback((skip, action, isDownloading) => {
    const yearsFromNow = selectedYear - currentYear;

    if (skip === 0) {
      resetUserCpdProgressReport();
    }

    if (isDownloading) {
      setDownloadingReport(true);

      setDownloadFinishedNoResult(false);
    }

    action({
      skip,
      yearsFromNow,
      cpdMonth: selectedMonth,
      cpdDay: selectedDay,
      take: queryUserStatsTakeNumber
    });
  }, [selectedYear, selectedMonth, selectedDay]);

  const userCpdProgressReportColumns = cpdProgressReportColumns(usersCpdStats);

  const userCpdProgressReportRows = cpdProgressReportRows(usersCpdStats, usersCpdCategoriesSearchTiles);

  const downloadedUserCpdProgressColumns = cpdProgressReportColumns(downloadedUsersCpdStats);

  const downloadedUserCpdProgressRows = cpdProgressReportRows(downloadedUsersCpdStats, downloadedUsersCpdCategoriesSearchTiles);

  const resetDownloadProgress = () => {
    setDownloadingReport(false);

    setDownloadingProgressPercentage(undefined);
    
    resetUserCpdProgressReport();
  };

  const triggerFileDownload = () => {
    downloadBlobStreamFile(
      writeDataToCsvStream(
        getCsvStream({
          header: true,
          columns: downloadedUserCpdProgressColumns.map(c => c.display())
        }),
        downloadedUserCpdProgressRows.map((row) => {
          return downloadedUserCpdProgressColumns.map((column) => {
            const key = column.columnName;

            return row[key];
          })
        })
      ).pipe(
        getBlobStream()
      ),
      `user-cpd-progress-report-${selectedYear}-${selectedMonth}-${selectedDay}.csv`
    );

    resetDownloadProgress();
  };

  // Effect that handles the User CPD Progress report download process.
  // Users CPD progresses are fetched in batch and assembled for file download.
  useEffect(() => {
    const loadedCount = downloadedUserCpdProgressRows.length;

    const moreToDownload = loadedCount < totalDownloadedUserCpdStats;

    // Make sure we are in downloading report process and previous downloading request is finished before load next batch.
    const shouldDownloadNextBatch = downloadingReport && !isDownloadingUserCpdProgressReport && moreToDownload;

    // Download is finished when no more to load and there is content loaded.
    // Note that this will be false if nothing returned from the load, i.e. total count is 0.
    const shouldTriggerFileDownload = loadedCount > 0 && !moreToDownload;

    // Make sure previous downloading request is finished before determine if the download returned any result or not.
    const downloadFinishedWithNoResult = loadedCount === 0 && totalDownloadedUserCpdStats === 0 && downloadingReport && !isDownloadingUserCpdProgressReport;

    const downloadProgressPercentage = `${((loadedCount / Math.max(totalDownloadedUserCpdStats, 1)) * 100).toFixed(0)}%`;

    setDownloadingProgressPercentage(downloadProgressPercentage);

    if (downloadFinishedWithNoResult) {
      resetDownloadProgress();

      setDownloadFinishedNoResult(true);
    }

    if (shouldDownloadNextBatch) {
      loadCpdProgressReport(loadedCount, downloadUserCpdProgressReport);
    }

    if (shouldTriggerFileDownload) {
      triggerFileDownload();
    }
  }, [isDownloadingUserCpdProgressReport, downloadingReport, downloadedUserCpdProgressRows]);

  return (
    <div className='base-layout'>
      <section className='layout--header'>
        <Grid>
          <Row className='button-gutter'>
            <Col xs={12}>
              <h1>User CPD Progress Report</h1>
            </Col>
          </Row>
        </Grid>
      </section>
      <section id='section--user-cpd-progress-report' className='layout--section'>
        <Grid>
          <Row className='no-gutter sectionHeader'>
            <Col xs={12}>
              <form>
                <div id='cpd-date-label'>
                  <div className='flexbox justify-between date-edit'>
                    <div className='select-year'>
                      <h4>CPD Year</h4>
                      <SelectFormField
                        label={null}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        emptyOption={false}
                        required
                        value={selectedYear}>
                        {
                          offsetYears.map((year, index) => (
                            <option key={`year-select-option-${index}`} value={year}>
                              {year}
                            </option>
                          ))
                        }
                      </SelectFormField>
                    </div>
                    <div className='select-month'>
                      <h4>CPD Month</h4>
                      <SelectFormField
                        label={null}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        emptyOption={false}
                        required
                        value={selectedMonth}>
                        {
                          Object.values(abbreviatedMonths).map((monthName, index) => (
                            <option key={`month-select-option-${index}`} value={index + 1}>
                              {monthName}
                            </option>
                          ))
                        }
                      </SelectFormField>
                    </div>
                    <div className='select-day'>
                      <h4>CPD Day</h4>
                      <SelectFormField
                        label={null}
                        onChange={(e) => setSelectedDay(e.target.value)}
                        emptyOption={false}
                        required
                        value={selectedDay}>
                        {
                          dayOptions.map((dayValue) => {
                            const day = dayValue + 1;

                            return (
                              <option key={`day-select-option-${day}`} value={day}>
                                {day}
                              </option>
                            )
                          })
                        }
                      </SelectFormField>
                    </div>
                  </div>
                </div>
              </form>
            </Col>
          </Row>
          <Row>
            <Col xs={4}>
              <LoadingButton loading={downloadingReport} onClick={() => loadCpdProgressReport(0, downloadUserCpdProgressReport, true)} text={`Download All Users CPD Progress Report`} />
              {
                downloadingReport && downloadingProgressPercentage !== undefined ? <h4>{downloadingProgressPercentage}</h4> : <React.Fragment></React.Fragment>
              }
            </Col>
            <Col xs={4}>
              <LoadingButton loading={isFetchingReport} onClick={() => loadCpdProgressReport(0, getUserCpdProgressReport)} text={`Show Users CPD Progress Report`} />
            </Col>
          </Row>
          {
            <Row className='no-gutter sectionHeader'>
              {
                userCpdProgressReportRows && userCpdProgressReportRows.length > 0 && !downloadingReport ?
                <Col xs={12} style={{ overflow: 'scroll' }}>
                  <DataTable
                    className='user-cpd-progress-report-table'
                    columns={userCpdProgressReportColumns}
                    rows={userCpdProgressReportRows}
                    isLoading={isFetchingReport}
                  />
                </Col> : <React.Fragment></React.Fragment>
              }
            </Row>
          }
          {
            <Row className='no-gutter sectionHeader'>
              {
                (userCpdProgressReport !== undefined && userCpdProgressReport.total === 0) || downloadFinishedNoResult ?
                <Col xs={12} style={{ marginTop: '20px' }}>
                  <h3>No User CPD progress report found for selected CPD date.</h3>
                </Col> : <React.Fragment></React.Fragment>
              }
            </Row>
          }
          {
            usersCpdStats.length < totalUserCpdStats && !downloadingReport ? 
              <Row className='no-gutter row-slider'>
                <Col xs={12}>
                  <LoadingButton loading={isFetchingReport} onClick={() => loadCpdProgressReport(usersCpdStats.length, getUserCpdProgressReport)} text={`Load More`} />
                </Col>
              </Row> : <div></div>
          }
        </Grid>
      </section>
    </div>
  )
};

const mapStateToProps = (state) => ({
  userCpdProgressReport: state.admin.report === undefined ? undefined : state.admin.report.userCpdProgressReport,
  downloadedUserCpdProgressReport: state.admin.report === undefined ? undefined : state.admin.report.downloadedUserCpdProgressReport,
  isFetchingReport: state.admin.isFetchingReport,
  isDownloadingUserCpdProgressReport: state.admin.isDownloadingUserCpdProgressReport
});

const mapDispatchToProps = (dispatch) => ({
  getUserCpdProgressReport: bindActionCreators(adminActions.admin.getUserCpdProgressReport, dispatch),
  downloadUserCpdProgressReport: bindActionCreators(adminActions.admin.downloadUserCpdProgressReport, dispatch),
  resetUserCpdProgressReport: bindActionCreators(adminActions.admin.resetUserCpdProgressReport, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(UserCpdProgressReport);



// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/user-cpd-progress-report.js